import useSWRMutation from "swr/mutation";
import { Network } from "@/utils/network";
import { zodUnsafeValidate } from "@/utils/zod";
import { InstagramSearch } from "../types";
import {
  getInstagramSearchInfluencersFilters,
  getInstagramSearchProfilesFilters,
  useInstagramSearch,
} from "./useInstagramSearch";
import {
  useInstagramSearchLists,
  type InstagramSearchListsResponse,
} from "./useInstagramSearchLists";

interface InstagramSearchCreateListProps {
  name: string;
  profiles: Set<InstagramSearch.Profile["id"]>;
  filters?:
    | ReturnType<typeof getInstagramSearchInfluencersFilters>
    | ReturnType<typeof getInstagramSearchProfilesFilters>;
}

export const useInstagramSearchCreateList = () => {
  const config = useInstagramSearch((state) => state.config);
  const clearSelection = useInstagramSearch((state) => state.clearSelection);
  const { mutate: mutateLists } = useInstagramSearchLists();

  return useSWRMutation<
    InstagramSearch.List,
    Error,
    string,
    InstagramSearchCreateListProps
  >(
    config.api.createList,
    async (api, { arg: props }) => {
      const { data } = await Network.post<InstagramSearch.List>(api, {
        name: props.name,
        profiles: Array.from(props.profiles),
        ...props.filters,
      });

      return zodUnsafeValidate(InstagramSearch.ListSchema, data);
    },
    {
      onSuccess: (list) => {
        mutateLists((data) => getUpdatedLists(data, list), false);

        clearSelection();
      },
    },
  );
};

const getUpdatedLists = (
  data: InstagramSearchListsResponse | undefined,
  list: InstagramSearch.List,
): InstagramSearchListsResponse | undefined => {
  if (data === undefined) {
    return undefined;
  }

  return {
    ...data,
    lists: [...data.lists, list],
  };
};
